import React, {useCallback, useMemo} from 'react';
import Form from 'react-bootstrap/Form';
import {AggregationChoice, aggregationChoicesFilters, EventFilter, EventFilterGroup} from '../utils/chartConstants';
import useData from '@/utils/useData';
import {FormikT, FormikTValues} from 'utils/types';
import {ChartQuery} from 'entities/chart';
import i18n from 'i18n';
import {SelectField, SelectOption} from '@/components/forms';
import Icon from '@/components/icons/Icon';

interface Props {
    values: FormikTValues<ChartQuery>;
    setFieldValue: FormikT<ChartQuery>['setFieldValue'];
    maxSelections?: number;
    eventFilters?: EventFilter[];
    eventFiltersLoading?: boolean;
}

const ChartAggregationsMultiSelect = React.memo<Props>((
    {
        values,
        setFieldValue,
        maxSelections,
        eventFilters: eventFiltersProp,
        eventFiltersLoading,
    },
) => {
    const [eventFilterGroupList, loadingFromData] = useData<EventFilterGroup>(window.URLS.eventFiltersResource, {
        storeResolver: 'eventFilters',
        actionResolver: 'loadEventFilters',
        useCachedData: true,
    });

    const eventFilters: EventFilter[] = eventFiltersProp ?? [
        ...(eventFilterGroupList.core || []),
        ...(eventFilterGroupList.prioritised || []),
        ...(eventFilterGroupList.non_prioritised || []),
        ...(eventFilterGroupList.others || []),
    ];
    const loading = eventFiltersProp !== undefined ? (eventFiltersLoading ?? false) : loadingFromData;

    const selectedItems = values.aggs_item_list ?? [];
    const placeholder = selectedItems.length
        ? i18n.t('chart_settings_choose_another_field.text')
        : i18n.t('chart_settings_choose_field.text');
    const selectedValues = useMemo(
        () => new Set(selectedItems.map((item) => item.value)),
        [selectedItems],
    );
    const options = useMemo(
        () => aggregationChoicesFilters[values.output ?? 'default'](eventFilters)
            .filter((el: EventFilter) => el[4] === true && !selectedValues.has(el[0]))
            .map((el: EventFilter) => ({
                value: el[0],
                label: el[1],
                type: el[2],
            })),
        [eventFilters, selectedValues, values.output],
    );

    const addAggregation = useCallback((option: SelectOption | null) => {
        if (!option) {
            return;
        }

        const next = [
            ...selectedItems,
            option as AggregationChoice,
        ];

        setFieldValue(
            'aggs_item_list',
            maxSelections ? next.slice(0, maxSelections) : next,
        );
    }, [maxSelections, selectedItems, setFieldValue]);

    const removeAggregation = useCallback((removedValue: string) => {
        setFieldValue(
            'aggs_item_list',
            selectedItems.filter((item) => item.value !== removedValue),
        );
    }, [selectedItems, setFieldValue]);

    const moveAggregation = useCallback((fromIndex: number, toIndex: number) => {
        if (toIndex < 0 || toIndex >= selectedItems.length) {
            return;
        }

        const next = [...selectedItems];
        const [movedItem] = next.splice(fromIndex, 1);
        next.splice(toIndex, 0, movedItem);
        setFieldValue('aggs_item_list', next);
    }, [selectedItems, setFieldValue]);

    return (
        <Form.Group className="cs-field" controlId="aggsItem" >
            <label className="cs-field__label">{i18n.t('jt_aggregate_based.text')}</label>
            <div className="chart-settings-aggregation-picker">
                <SelectField
                    compact
                    name="aggs_item_list_add"
                    value={null}
                    onChange={addAggregation}
                    options={options}
                    placeholder={placeholder}
                    isClearable={false}
                    isSearchable
                    maxMenuHeight={180}
                    menuPlacement="auto"
                    menuShouldScrollIntoView={true}
                    customStyles={{
                        control: (base: any) => ({...base, minHeight: 34, height: 34}),
                        valueContainer: (base: any) => ({...base, height: 34, padding: '0 14px'}),
                        input: (base: any) => ({...base, margin: 0, padding: 0}),
                        indicatorsContainer: (base: any) => ({
                            ...base,
                            height: 34,
                            alignSelf: 'center',
                        }),
                        menu: (base: any) => ({...base, marginBottom: 8}),
                        menuList: (base: any) => ({...base, maxHeight: 180}),
                        option: (base: any, state: any) => ({
                            ...base,
                            background: state.isFocused ? 'rgba(0, 158, 251, 0.08)' : 'transparent',
                            backgroundColor: state.isFocused ? 'rgba(0, 158, 251, 0.08)' : 'transparent',
                            color: 'inherit',
                            cursor: 'pointer',
                            ':active': {
                                ...base[':active'],
                                backgroundColor: 'rgba(0, 158, 251, 0.08)',
                            },
                        }),
                    }}
                    isDisabled={loading || (!!maxSelections && selectedItems.length >= maxSelections)}
                />
                <div className="chart-settings-multi-filter__chips chart-settings-aggregation-picker__chips">
                    {selectedItems.map((item, index) => (
                        <span className="chart-settings-multi-filter__chip" key={item.value}>
                            {index > 0 ? (
                                <button
                                    type="button"
                                    className="chart-settings-multi-filter__chip-action"
                                    onClick={() => moveAggregation(index, index - 1)}
                                    aria-label={i18n.t('chart_settings_move_field_left.text', {field: item.label})}
                                >
                                    <Icon name="mdi-chevron-left" size="12px" />
                                </button>
                            ) : null}
                            <span className="chart-settings-multi-filter__chip-label">
                                {item.label}
                            </span>
                            {index < selectedItems.length - 1 ? (
                                <button
                                    type="button"
                                    className="chart-settings-multi-filter__chip-action"
                                    onClick={() => moveAggregation(index, index + 1)}
                                    aria-label={i18n.t('chart_settings_move_field_right.text', {field: item.label})}
                                >
                                    <Icon name="mdi-chevron-right" size="12px" />
                                </button>
                            ) : null}
                            <button
                                type="button"
                                className="chart-settings-multi-filter__chip-remove"
                                onClick={() => removeAggregation(item.value)}
                                aria-label={i18n.t('ft_remove_filter.text')}
                            >
                                <Icon name="mdi-close" size="12px" />
                            </button>
                        </span>
                    ))}
                </div>
                {selectedItems.length >= 2 ? (
                    <div className="cs-helper-text chart-settings-aggregation-picker__reorder-hint">
                        {i18n.t('chart_settings_aggregation_reorder_hint.text')}
                    </div>
                ) : null}
            </div>
        </Form.Group >
    );
});

export default ChartAggregationsMultiSelect;
